/**
 * Queue tilt (TradeFinder Context): the flow-signals `queue_tilt` line, the
 * smoothed top-of-book queue imbalance (bid_size - ask_size) / (bid_size +
 * ask_size) in [-1, 1]. Positive = bid queue heavier = the thin ask tends to
 * get eaten next (the next tick is more likely up).
 *
 * Research (full history, 2024-2025 MBO, next-5 s direction): ES 67.4%,
 * MES 65.6%, ES's book applied to MES 61.2%; NQ / MNQ ~53% (nothing). So it is
 * drawn on ES and MES only. An MES chart reads its OWN book first (the
 * stronger read on history) and ES's as a second, thinner row; flow-signals
 * publishes the line for ES / MES only by default.
 *
 * Drawn two ways (QueueTiltBand.tsx): a per-bar ribbon along the bottom of
 * the price pane (time-weighted mean tilt per displayed bar, readable at a
 * glance on a phone) and a band at the price scale with the live value.
 * Pure helpers here.
 */
import { normalizeToBase } from '../../utils/symbolUtils';
import type { FlowLinePoint } from '../../store/flowLinesStore';

export const QUEUE_TILT_LINE = 'queue_tilt' as const;

/** |tilt| at or above this is the "strong" region the research used. */
export const QUEUE_TILT_STRONG = 0.5;
/** Newest point older than this (vs the wall clock) hides the band, ms. */
export const QUEUE_TILT_STALE_MS = 10_000;
/** Band opacity near zero tilt (nearly transparent) and at / past QUEUE_TILT_STRONG. */
export const QUEUE_TILT_MIN_OPACITY = 0.06;
export const QUEUE_TILT_MAX_OPACITY = 0.95;

export type QueueTiltRoot = 'ES' | 'MES';

export interface QueueTiltRoots {
  /** The chart's own book. */
  primary: QueueTiltRoot;
  /** A second read drawn beside it (ES's book on an MES chart), else null. */
  secondary: QueueTiltRoot | null;
}

/** ES -> ES only; MES -> MES, with ES as the second read; anything else -> null (no tilt). */
export function queueTiltRoots(symbol: string): QueueTiltRoots | null {
  const base = normalizeToBase(symbol || '');
  if (base === 'ES') return { primary: 'ES', secondary: null };
  if (base === 'MES') return { primary: 'MES', secondary: 'ES' };
  return null;
}

/** The root whose tilt a chart shows first (its own), null when it shows none. */
export function queueTiltSourceRoot(symbol: string): QueueTiltRoot | null {
  return queueTiltRoots(symbol)?.primary ?? null;
}

export interface QueueTiltReading {
  value: number;
  /** Exchange time of the point, ms. */
  ts: number;
}

/**
 * The newest point, unless it is missing, not finite, or older than `staleMs`
 * relative to `now` (wall clock): then null and the band hides.
 */
export function latestQueueTilt(
  points: readonly FlowLinePoint[] | undefined,
  now: number,
  staleMs = QUEUE_TILT_STALE_MS,
): QueueTiltReading | null {
  if (!points || points.length === 0) return null;
  const [ts, value] = points[points.length - 1];
  if (!Number.isFinite(ts) || !Number.isFinite(value)) return null;
  if (now - ts > staleMs) return null;
  return { value: Math.max(-1, Math.min(1, value)), ts };
}

export interface QueueTiltStyle {
  color: string;
  /** 0..1: MIN at zero, MAX at |tilt| >= QUEUE_TILT_STRONG, linear between. */
  opacity: number;
  strong: boolean;
  /** "▲ 0.62" / "▼ 0.41" / "0.00". */
  label: string;
}

/**
 * Colour + intensity: `colors.low` (green) for a positive tilt, `colors.high`
 * (red) for negative (levelsStore `flowQueueTilt`, the bullish-low /
 * bearish-high convention of every flow colour).
 */
export function queueTiltStyle(tilt: number, colors: { high: string; low: string }): QueueTiltStyle {
  const t = Number.isFinite(tilt) ? Math.max(-1, Math.min(1, tilt)) : 0;
  const mag = Math.abs(t);
  const k = Math.min(1, mag / QUEUE_TILT_STRONG);
  const arrow = t > 0 ? '▲ ' : t < 0 ? '▼ ' : '';
  return {
    color: t < 0 ? colors.high : colors.low,
    opacity: QUEUE_TILT_MIN_OPACITY + k * (QUEUE_TILT_MAX_OPACITY - QUEUE_TILT_MIN_OPACITY),
    strong: mag >= QUEUE_TILT_STRONG,
    label: `${arrow}${mag.toFixed(2)}`,
  };
}

/** One ribbon cell: a displayed bar (start, unix s) and its mean tilt. */
export interface TiltCell {
  time: number;
  value: number;
}

/**
 * Time-weighted mean tilt per displayed bar. A point holds until the next one
 * (the service sends on change, else every 5 s), at most QUEUE_TILT_STALE_MS;
 * its whole span counts to the bar it starts in. Bars with no point get no
 * cell (a gap, not a zero). Ascending by time.
 */
export function tiltCells(points: readonly FlowLinePoint[] | undefined, tfMinutes: number): TiltCell[] {
  const out: TiltCell[] = [];
  if (!points || points.length === 0) return out;
  const step = Math.max(60_000, tfMinutes * 60_000);
  let bar = Number.NaN;
  let sum = 0;
  let w = 0;
  const push = () => {
    if (w > 0) out.push({ time: Math.floor(bar / 1000), value: Math.max(-1, Math.min(1, sum / w)) });
  };
  for (let i = 0; i < points.length; i += 1) {
    const [ts, v] = points[i];
    if (!Number.isFinite(ts) || !Number.isFinite(v)) continue;
    const next = i + 1 < points.length ? points[i + 1][0] : ts + 1_000;
    const dt = Math.min(QUEUE_TILT_STALE_MS, Math.max(1, next - ts));
    const b = Math.floor(ts / step) * step;
    if (b !== bar) {
      push();
      bar = b;
      sum = 0;
      w = 0;
    }
    sum += v * dt;
    w += dt;
  }
  push();
  return out;
}

/** "agrees" when both reads lean the same way, "disagrees" when opposite, null when either is flat or missing. */
export function tiltAgreement(primary: number | null, secondary: number | null): 'agrees' | 'disagrees' | null {
  if (primary === null || secondary === null || primary === 0 || secondary === 0) return null;
  return Math.sign(primary) === Math.sign(secondary) ? 'agrees' : 'disagrees';
}
